import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { setApplicationQuoteUseVehicle, updateEditingApp } from '../../../store/application/actions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import WarningIcon from "@mui/icons-material/Warning";
import { IApplicationQuote, IRuleViolation } from '../../../Interfaces/Application/IApplicationQuote';
import { useBusinessRulesEngine } from '../../../Data/BusinessRulesData';
import { ApplicationDispatch } from '../../../store/application/types';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../store/rootReducer';
import { NumberFormatCustom } from '../../Shared/NumberFormatCustom';
import MenuItem from '@mui/material/MenuItem';
import { IApplication } from '../../../Interfaces/Application/IApplication';
import { showValidationDialog } from '../../../utils/ValidationDialog';
import { Constants } from '../../../utils/constants';
import {
  calculateAmountToFinanceFromPayment,
  calculateBalloonPayment,
  calculateLTV,
  calculatePayment,
  debtToIncome,
  getAmortizationSchedule,
  getAmountToFinance,
  getCashDown,
  getIncomeAndDebtBreakdownInternal,
  getQuoteModifiers,
  paymentToIncome
} from '../../../utils/quoteUtils';
import { IQuotePayType } from '../../../Interfaces/Common/IQuotePayType';
import { usePricingPrograms } from '../../../Data/LookupData';
import { useConfiguration } from '../../../Data/ConfigurationData';
import { copyQuote, IQuote } from '../../../Interfaces/Common/IQuote';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { getHelperText, getHelperTextProps } from './OverviewHelpers';
import Big from 'big.js';
import { nullOrPositiveNumber } from '../../../utils/utils';
import NumberFormatNumericTextBox from '../../NumberFormatNumericTextBox/NumberFormatNumericTextBox';
import { NumericTextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Checkbox, Tooltip } from '@mui/material';
import { isNil } from 'ramda';
import { IAppCustomer } from '../../../Interfaces/Application/IAppCustomer';
import { ICustomerIncomeDebt } from '../../../Interfaces/Customer/ICustomerIncomeDebt';
import produce from 'immer';
import { IndirectQuoteType } from "../../../Data/IndirectQuoteType";
import {add, divide, multiply} from "@amsanalytics/react-shared-library";

export interface OverviewQuoteProps {
  applicationQuote: IApplicationQuote;
  updateApplication: (fn: (app: IApplication) => IApplication | void) => void;
  i: number;
  showMoreQuoteFields: boolean;
  isDealerRetailQuote?: boolean;
}

export const OverviewQuote: React.FC<OverviewQuoteProps> = (props) => {
  const applicationQuote = props.applicationQuote;
  const i = props.i;
  const updateApplication = props.updateApplication;
  const dispatch = useDispatch<ApplicationDispatch>();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const payTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const businessRulesEngine = useBusinessRulesEngine();
  const applicationBudget = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget);
  const { data: pricingPrograms } = usePricingPrograms();
  const quotePayTypes: IQuotePayType[] = useSelector((state: AppState) => state.applications.quotePayTypes || []);
  const { data: configuration } = useConfiguration();
  const incomeRows = useSelector((state: AppState) => state.applications.editingApp?.applicationBudget?.incomeRows) || [];
  const showWarnings = (warnings: IRuleViolation[]) => {
    showValidationDialog({
      errors: warnings,
      callback: () => { },
      allowBypass: false
    });
  };

  const primaryApplicantIncomeAndDebtInfo = currentApp?.appCustomers
    ?.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId)
    ?.customer?.customerIncomeDebt[0];

  const appQuote = props.applicationQuote;
  const quote = appQuote.quote;
  const appSourceIsDealerFlow = currentApp?.appSource?.toUpperCase() === "DEALER FLOW";
  useEffect(() => {
    if (quote && (configuration?.featureFlags?.[FeatureFlagIds.UseBusinessRulePaymentCalculation] !== true || props.isDealerRetailQuote === true)) {
      const payType: IQuotePayType | undefined = quotePayTypes.find(qpt => qpt.id === quote.quotePayTypeId);
      let payment: number | undefined = undefined;
      let additionalMonthlyPayment = 0;
      if (quote.amountToFinance && quote.apr && quote.term && payType) {
        const financeTerm = quote.term + (quote.balloonTerm ?? 0);
        if (configuration?.featureFlags?.[FeatureFlagIds.Use45DayFirstPayment] === true) {
          const amortizationSchedule = getAmortizationSchedule(quote);

          if (amortizationSchedule.length > 1) {
            const additionalInterest = multiply(add(amortizationSchedule[0].interest, amortizationSchedule[1].interest), 0.25);
            additionalMonthlyPayment = divide(additionalInterest, financeTerm);
          }
          payment = add(
            calculatePayment(quote.apr, payType.paymentsPerYear, financeTerm, quote.amountToFinance),
            additionalMonthlyPayment
          );
        } else {
          payment = calculatePayment(quote.apr, payType.paymentsPerYear, financeTerm, quote.amountToFinance);
        }

        if (payment && quote.payment !== payment) {
          updateApplication((app) => {
            const q = app.applicationQuotes.find((x) => x.quoteId === quote.id)?.quote;
            if (q) {
              q.payment = payment;
            }
          });
        }
      }
    }
  }, [props.applicationQuote]);

  const submittedIncome = currentApp?.appCustomers.map(ac => ac.customer.customerEmployment.find(ce => ce.primaryEmployment)?.income) ?? [0];
  const totalGrossIncome = submittedIncome.reduce((totalSubmittedIncome, income) => (totalSubmittedIncome ?? 0) + (income ?? 0));

  const incomeAndDebtInfo = applicationBudget
    ?
    getIncomeAndDebtBreakdownInternal(
      applicationBudget,
      currentApp?.appCustomers || [],
      totalGrossIncome,
      currentApp?.includeAllCurrentIncomes,
      incomeRows?.find(r => r.name === 'Additional Income')?.isAutomaticallyCalculated || false)
    : undefined;


  // TODO change this so that it's not using the index to determine the column name
  const header =
    (!configuration) ? '' :
      (configuration.isFinanceCompany) ? (i === 0 ? 'Calculated' : 'Quote') :
        `Option ${i + 1}`;

  const updateAmountToFinance = (q: IQuote) => {
    if (q.carSalePrice) {
      const amountToFinance = getAmountToFinance(q, q.carSalePrice);
      q.amountToFinance = amountToFinance;
    }
  }

  const updateCashDown = (q: IQuote) => {
    if (q.carSalePrice) {
      q.cashDown = getCashDown(q);
    }
  }

  const disableRate = () => {

    if (applicationQuote.quote.quoteType === IndirectQuoteType.Recommended) {
      return true;
    }

    if (configuration?.featureFlags[FeatureFlagIds.AllowRateEdit] === true) {
      return false;
    }

    if (configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true) {
      return true;
    }
  }

  const disableTerm = () => {

    if (applicationQuote.quote.quoteType === IndirectQuoteType.Recommended) {
      return true;
    }

    if (configuration?.featureFlags[FeatureFlagIds.AllowTermEdit] === true) {
      return false;
    }

    if (configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true) {
      return true;
    }
  }

  const anyQuoteHasModifiers = () => {
    const result = currentApp?.applicationQuotes?.some(x => getQuoteModifiers(x.quote) !== 0);
    return result;
  }

  const calculateDebtToIncomeForQuote = () => {
    if (incomeAndDebtInfo && quote && quote.payment && incomeAndDebtInfo.totalIncomeUsed && incomeAndDebtInfo.totalDebtUsed) {
      if (quote.quoteType === IndirectQuoteType.Recommended && incomeAndDebtInfo.paymentToSubmit && quote.payment) {
        const totalDebtUsed = new Big(incomeAndDebtInfo.totalDebtUsed);
        const totalDebtMinusSubmittedPayment = totalDebtUsed.sub(incomeAndDebtInfo.paymentToSubmit);

        const totalDebt = totalDebtMinusSubmittedPayment.add(Big(configuration?.featureFlags?.[FeatureFlagIds.ExcludePaymentFromDebtToIncome] === true ? 0 : quote.payment));
        return `${debtToIncome(Number.parseFloat(totalDebt.toFixed(7)), incomeAndDebtInfo.totalIncomeUsed).toFixed(2)}%`;
      } else {
        return `${debtToIncome(incomeAndDebtInfo.totalDebtUsed, incomeAndDebtInfo.totalIncomeUsed).toFixed(2)}%`;
      }
    }
    return '';
  }

  const vehicleType = configuration?.featureFlags?.[FeatureFlagIds.TypeOfVehicleRv] ? "RV" : "Car";

  const disablePricingProgram = (q: IQuote): boolean => {
    const pricingProgram = pricingPrograms?.find(pp => pp.id === q.pricingProgramId);
    if (pricingProgram) {
      return q.quoteType === IndirectQuoteType.Recommended && pricingProgram.allowChangesWhenQuoteIsAutomaticallyCalculated !== true;
    }
    return false; // Don't disable it if it is not set
  }

  const allowTierOverride = (q: IQuote): boolean => {
    return q.quoteType === IndirectQuoteType.Recommended && configuration?.featureFlags[FeatureFlagIds.AllowCalculatedTierOverride] === true;
  }

  const selectedAmsPayTypePaymentsPerYear = payTypes.find(pt => pt.id === quote.quotePayTypeId)?.paymentsPerYear;

  return (
    <Grid item xs={12} sm={6} md={4} container spacing={1} paddingTop={1.5} paddingBottom={1.5} style={{ width: '100%', maxWidth: '375px' }}>
      {/* Header */}
      <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Typography variant='h6' style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
          {header}
        </Typography>
        {currentApp?.vehicle && (
          <FormControlLabel
            label='Vehicle'
            control={
              <Switch
                size='small'
                color='primary'
                disabled={appSourceIsDealerFlow || configuration?.featureFlags[FeatureFlagIds.DisableVehicleOnQuote] === true}
                checked={!appSourceIsDealerFlow && applicationQuote.useVehiclePrice}
                onChange={(e) => {
                  updateApplication((app) => {
                    const aq = app.applicationQuotes[i];
                    if (aq.useVehiclePrice) {
                      updateAmountToFinance(aq.quote);
                    }
                  });
                  dispatch(
                    setApplicationQuoteUseVehicle(
                      {
                        applicationQuoteId: applicationQuote.id,
                        useVehiclePrice: e.target.checked,
                      },
                      businessRulesEngine
                    )
                  );
                }}
              />
            }
          />
        )}
        {((applicationQuote && applicationQuote.ruleViolations && applicationQuote.ruleViolations.length > 0)
          || applicationQuote.quote.quoteType === IndirectQuoteType.Underwriter) && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                flexWrap: 'nowrap',
                flexDirection: 'row',
              }}
            >
              {applicationQuote && applicationQuote.ruleViolations && applicationQuote.ruleViolations.length > 0 && (
                <IconButton
                  size='small'
                  // style={{padding: '0px 0px 0px 12px'}}
                  onClick={() => {
                    showWarnings(applicationQuote.ruleViolations || []);
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                  edge='end'
                >
                  <WarningIcon color='error' fontSize='small' />
                </IconButton>
              )}
              {applicationQuote.quote.quoteType === IndirectQuoteType.Underwriter && configuration?.isFinanceCompany && (
                <Tooltip title={`Copy the values from the 'Calculated' quote to the quote estimator. Note: This is not available for DealerFlow Retail. It is available for Payment Call.`}>
                  <div> {/* This div is needed, otherwise the tooltip will be hidden when the IconButton is disabled */}
                    <IconButton
                      size='small'
                      disabled={props.isDealerRetailQuote === true && i === 1} // Disable the sync button for the dealer retail quote. This should only be enabled in dealer flow if they are using payment call. 
                      onClick={() => {
                        updateApplication((app) => {
                          const copyFromApplicationQuote = app.applicationQuotes.find(
                            (aq) => aq.quote.quoteType === IndirectQuoteType.Recommended
                          );
                          const applicationQuote = app.applicationQuotes.find((x) => x.quoteId === quote.id);
                          if (applicationQuote && copyFromApplicationQuote?.quote) {
                            applicationQuote.quote = copyQuote(copyFromApplicationQuote.quote, applicationQuote.quoteId, IndirectQuoteType.Underwriter);
                            applicationQuote.useVehiclePrice = copyFromApplicationQuote.useVehiclePrice;
                            applicationQuote.quote.cashDown = Math.max(copyFromApplicationQuote.quote.cashDown ?? 0, primaryApplicantIncomeAndDebtInfo?.downpayment ?? 0)
                          }
                        });
                      }}
                    >
                      <SyncAltIcon color={props.isDealerRetailQuote === true && i === 1 ? 'disabled' : 'primary'} fontSize='small' />
                    </IconButton>
                  </div>
                </Tooltip>
              )}
            </div>
          )}
      </div>

      {/* Show Pricing Program if Feature Flag is ON */}
      {configuration?.featureFlags?.[FeatureFlagIds.ShowPricingProgram] === true && (
        <Grid item xs={allowTierOverride(applicationQuote.quote) ? 8 : 12}>
          <TextField
            style={{ textAlign: 'right' }}
            label='Pricing Program'
            value={applicationQuote.quote.pricingProgramId || ''}
            onChange={(e) => {
              updateApplication((app) => {
                // TODO: Refactor - dispatch action
                app.applicationQuotes[i].quote.pricingProgramId = e.target.value;
              });
            }}
            fullWidth
            size='small'
            variant='outlined'
            inputProps={{ style: { textAlign: 'right' } }}
            select
            disabled={disablePricingProgram(applicationQuote.quote) || applicationQuote.quote.overrideCalculatedTier !== true}
          >
            <MenuItem>&nbsp;</MenuItem>
            {/* These hardcoded ones are there for CFI and UDR to beable to select/change the program */}
            {pricingPrograms
              ?.filter(
                (pp) =>
                  applicationQuote.quote.quoteType === IndirectQuoteType.Recommended ||
                  applicationQuote.quote.pricingProgramId?.toUpperCase() === pp.id.toUpperCase() ||
                  pp.id.toUpperCase() === '44EDFCAD-3BA6-41E3-A386-284343D8723B' ||
                  pp.id.toUpperCase() === 'F2AD2179-4666-4665-BC21-2F730B46F424'
              )
              .map((x, key) => (
                <MenuItem key={key} value={x.id || ''}>
                  {x.name}
                </MenuItem>
              ))}
          </TextField>
        </Grid>
      )}

      {allowTierOverride(applicationQuote.quote) && (
        <Grid item xs={4}>
          <FormControlLabel
            value="top"
            control={<Checkbox size="small" color="primary" />}
            label="Override"
            labelPlacement="end"
            checked={applicationQuote.quote.overrideCalculatedTier === true}
            onChange={(e, checked) => {
              updateApplication((app) => {
                const aq = app.applicationQuotes[i].quote;
                aq.overrideCalculatedTier = checked;
              })
            }}
          />
        </Grid>
      )}


      {/* NADA */}
      {currentApp?.applicationQuotes?.some((x) => x.useVehiclePrice) && (
        <Grid item xs={12}>
          <NumberFormatNumericTextBox
            label={configuration?.featureFlags[FeatureFlagIds.UseCarTheoInsteadOfNada] ? 'Car Theo' : 'NADA/ACV'}
            style={{ visibility: applicationQuote.useVehiclePrice ? 'visible' : 'hidden' }}
            value={applicationQuote.quote.nadavalue}
            onChange={(e: NumericTextBoxChangeEvent) => {
              updateApplication((app) => {
                app.applicationQuotes[i].quote.nadavalue = e.value ?? undefined;
              });
            }}
            numberType={'decimal'}
            error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'nadavalue') !== undefined}
            validationMessage={getHelperText('nadavalue', props.applicationQuote, currentApp)}
            disabled={configuration?.isFinanceCompany && i === 0}
          />
        </Grid>
      )}

      {/* LTV */}
      {currentApp?.applicationQuotes?.some((x) => x.useVehiclePrice) && (
        <Grid item xs={12}>
          <TextField
            label='LTV'
            style={{ visibility: applicationQuote.useVehiclePrice ? 'visible' : 'hidden' }}
            value={calculateLTV(applicationQuote.quote, currentApp.vehicle, true)}
            fullWidth
            size='small'
            variant='outlined'
            inputProps={{ style: { textAlign: 'right' } }}
            error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'ltv') !== undefined}
            helperText={getHelperText('ltv', props.applicationQuote, currentApp)}
            FormHelperTextProps={getHelperTextProps('ltv', props.applicationQuote)}
            disabled={true}
          />
        </Grid>
      )}

      {/* Income */}
      <Grid item xs={12}>
        <TextField
          label='Income'
          value={incomeAndDebtInfo ? incomeAndDebtInfo.totalIncomeUsed || '' : ''}
          fullWidth
          size='small'
          variant='outlined'
          InputProps={{
            inputComponent: NumberFormatCustom as any,
          }}
          inputProps={{ style: { textAlign: 'right' } }}
          // disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
          disabled={true}
        />
      </Grid>

      {/* Payment to Income (Net) */}
      <Grid item xs={12}>
        <TextField
          label='Payment to Income (Net)'
          value={
            incomeAndDebtInfo && quote && quote.payment
              ? `${paymentToIncome(quote.payment, selectedAmsPayTypePaymentsPerYear, incomeAndDebtInfo.totalIncomeUsed).toFixed(2)}%`
              : ''
          }
          fullWidth
          size='small'
          variant='outlined'
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'paymentToIncome') !== undefined}
          helperText={getHelperText('paymentToIncome', props.applicationQuote, currentApp)}
          FormHelperTextProps={getHelperTextProps('paymentToIncome', props.applicationQuote)}
          inputProps={{ style: { textAlign: 'right' } }}
          disabled={true}
        />
      </Grid>
      {/* Payment to Income (Gross) */}
      <Grid item xs={12}>
        <TextField
          label='Payment to Income (Gross)'
          value={
            incomeAndDebtInfo && quote && quote.payment
              ? `${paymentToIncome(quote.payment, selectedAmsPayTypePaymentsPerYear, incomeAndDebtInfo.totalGrossIncome).toFixed(2)}%`
              : ''
          }
          fullWidth
          size='small'
          variant='outlined'
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'paymentToIncome') !== undefined}
          helperText={getHelperText('paymentToIncome', props.applicationQuote, currentApp)}
          FormHelperTextProps={getHelperTextProps('paymentToIncome', props.applicationQuote)}
          inputProps={{ style: { textAlign: 'right' } }}
          disabled={true}
        />
      </Grid>

      {/* Debt to Income */}
      <Grid item xs={12}>
        <TextField
          label='Debt to Income'
          value={calculateDebtToIncomeForQuote()}
          disabled
          fullWidth
          size='small'
          variant='outlined'
          inputProps={{ style: { textAlign: 'right' } }}
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'debtToIncome') !== undefined}
          helperText={getHelperText('debtToIncome', props.applicationQuote, currentApp)}
          FormHelperTextProps={getHelperTextProps('debtToIncome', props.applicationQuote)}
        />
      </Grid>

      {/* Car Sale Price */}
      {currentApp?.applicationQuotes?.some((x) => x.useVehiclePrice) && (
        <Grid item xs={12}>
          <TextField
            style={{ visibility: appQuote.useVehiclePrice ? 'visible' : 'hidden' }}
            label={`${vehicleType} Sale Price`}
            value={appQuote?.quote?.carSalePrice || ''}
            onChange={(e) => {
              updateApplication((app) => {
                const newValue = parseFloat(e.target.value) || undefined;
                const aq = app.applicationQuotes[i];
                aq.quote.carSalePrice = newValue;
                if (aq.useVehiclePrice) {
                  updateAmountToFinance(aq.quote);
                }
              });
            }}
            fullWidth
            size='small'
            variant='outlined'
            InputProps={{
              inputComponent: NumberFormatCustom as any,
            }}
            inputProps={{ style: { textAlign: 'right' } }}
            error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'carSalePrice') !== undefined}
            helperText={getHelperText('carSalePrice', props.applicationQuote, currentApp)}
            FormHelperTextProps={getHelperTextProps('carSalePrice', props.applicationQuote)}
            disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
          />
        </Grid>
      )}

      {/* Extra Quote Fields placeholder */}
      {!props.showMoreQuoteFields && anyQuoteHasModifiers() && (
        <Grid item xs={12}>
          <TextField
            style={{ visibility: 'hidden' }}
            label='Additional Modifiers'
            value={getQuoteModifiers(quote)}
            fullWidth
            size='small'
            variant='outlined'
            InputProps={{
              inputComponent: NumberFormatCustom as any,
            }}
            inputProps={{ style: { textAlign: 'right' } }}
            disabled={true}
          />
        </Grid>
      )}

      {props.isDealerRetailQuote !== true && props.showMoreQuoteFields && (
        <>
          {/* Discount */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Discount'
              value={appQuote?.quote?.discount || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.discount = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'discount') !== undefined}
              helperText={getHelperText('discount', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('discount', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>

          {/* Deferred Down */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Deferred Down'
              value={appQuote?.quote?.deferredDown || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.deferredDown = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'deferredDown') !== undefined}
              helperText={getHelperText('deferredDown', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('deferredDown', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>

          {/* Trade Allowance */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Trade Allowance'
              value={appQuote?.quote?.tradeAllowance || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.tradeAllowance = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'tradeAllowance') !== undefined}
              helperText={getHelperText('tradeAllowance', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('tradeAllowance', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>

          {/* Trade Lien */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Trade Lien'
              value={appQuote?.quote?.tradeLien || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.tradeLien = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'tradeLien') !== undefined}
              helperText={getHelperText('tradeLien', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('tradeLien', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>

          {/* Fees */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Fees'
              value={appQuote?.quote?.fees || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.fees = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'fees') !== undefined}
              helperText={getHelperText('fees', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('fees', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>

          {/* Taxes */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Taxes'
              value={appQuote?.quote?.taxes || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.taxes = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'taxes') !== undefined}
              helperText={getHelperText('taxes', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('taxes', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>

          {/* Other Misc */}
          <Grid item xs={12}>
            <TextField
              style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
              label='Other Misc'
              value={appQuote?.quote?.other || ''}
              onChange={(e) => {
                updateApplication((app) => {
                  const newValue = parseFloat(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  aq.quote.other = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'other') !== undefined}
              helperText={getHelperText('other', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('other', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Grid>
        </>
      )}

      {/* Cash Down */}
      <Grid container item xs={12}>
        <Grid item xs={6}>
          <Tooltip title='Cash the customer said they would put down.'>
            <TextField
              label='Cash Down'
              value={primaryApplicantIncomeAndDebtInfo?.downpayment || 0}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              disabled={isNil(props.applicationQuote.quote.quoteType === IndirectQuoteType.Recommended) || props.applicationQuote.quote.quoteType === IndirectQuoteType.Underwriter}
              onChange={(e) => {
                const updatedApp = produce(currentApp, (appToUpdate) => {
                  const primaryAppCustomer: IAppCustomer | undefined = appToUpdate?.appCustomers?.find(ac => ac.customerRoleId.toUpperCase() === Constants.roleApplicantId);
                  if (primaryAppCustomer) {
                    const primaryAppCustomerIncomeDebt: ICustomerIncomeDebt | undefined = primaryAppCustomer?.customer?.customerIncomeDebt[0];
                    if (primaryAppCustomerIncomeDebt) {
                      primaryAppCustomerIncomeDebt.downpayment = nullOrPositiveNumber(e.target.value) || 0;
                    }
                  }
                });
                dispatch(updateEditingApp(updatedApp!, businessRulesEngine));
              }}
            />
          </Tooltip>
        </Grid>
        <Grid item xs={6}>
          <Tooltip title='The actual cash down required to finance.'>
            <TextField
              label='Cash Needed'
              value={applicationQuote.quote.cashDown || 0}
              onChange={(e) => {
                // TODO: Refactor - dispatch action
                updateApplication((app) => {
                  const newValue = nullOrPositiveNumber(e.target.value) || undefined;
                  const aq = app.applicationQuotes[i];
                  const cashDownDiff = (newValue ?? 0) - (aq.quote.cashDown ?? 0);
                  aq.quote.cashDown = newValue;
                  if (aq.useVehiclePrice) {
                    updateAmountToFinance(aq.quote);
                  } else if (appSourceIsDealerFlow && aq.quote.quoteType === IndirectQuoteType.Underwriter) {
                    // Need to recaluculate the amount to finance from the dealer flow column values
                    aq.quote.amountToFinance = (aq.quote.amountToFinance ?? 0) - cashDownDiff;
                  }
                });
              }}
              fullWidth
              size='small'
              variant='outlined'
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              inputProps={{ style: { textAlign: 'right' } }}
              error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'cashDown') !== undefined}
              helperText={getHelperText('cashDown', props.applicationQuote, currentApp)}
              FormHelperTextProps={getHelperTextProps('cashDown', props.applicationQuote)}
              disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
            />
          </Tooltip>
        </Grid>
      </Grid>
      {/* Lender Fees */}
      <Grid item xs={12}>
        <NumberFormatNumericTextBox
          label={'Lender Fees'}
          value={applicationQuote.quote.lenderFees}
          onChange={(e: NumericTextBoxChangeEvent) => {
            const lenderFeesDiff = (e.value ?? 0) - (applicationQuote.quote.lenderFees ?? 0);
            updateApplication((app) => {
              const aq = app.applicationQuotes[i];
              aq.quote.lenderFees = e.value ?? undefined;
              if (aq.useVehiclePrice) {
                updateAmountToFinance(aq.quote);
              } else if (appSourceIsDealerFlow && aq.quote.quoteType === IndirectQuoteType.Underwriter) {
                // Need to recalculate the amount to finance from the dealer flow column values
                aq.quote.amountToFinance = (aq.quote.amountToFinance ?? 0) + lenderFeesDiff;
              }
            });
          }}
          numberType={'decimal'}
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'lenderFees') !== undefined}
          validationMessage={getHelperText('lenderFees', props.applicationQuote, currentApp)}
          disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended || configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true}
        />
      </Grid>
      {/* Amount to Finance */}
      <Grid item xs={12}>
        <NumberFormatNumericTextBox
          label={'Amount to Finance'}
          value={applicationQuote.quote.amountToFinance}
          onChange={(e: NumericTextBoxChangeEvent) => {
            updateApplication((app) => {
              const aq = app.applicationQuotes[i];
              aq.quote.amountToFinance = e.value ?? undefined;
              if (aq.useVehiclePrice) {
                updateCashDown(aq.quote);
              }
            });
          }}
          numberType={'decimal'}
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'amountToFinance') !== undefined}
          validationMessage={getHelperText('amountToFinance', props.applicationQuote, currentApp)}
          disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended || configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true || props.isDealerRetailQuote === true}
        />
      </Grid>
      {props.isDealerRetailQuote !== true && <Grid item xs={12}>
        <TextField
          label='Dealer/Acquisition Fee'
          style={{ visibility: configuration?.isFinanceCompany ? 'visible' : 'hidden' }}
          value={applicationQuote.quote.bankFee || ''}
          onChange={(e) => {
            updateApplication((app) => {
              const newValue = Number.parseFloat(e.target.value) || 0;
              const aq = app.applicationQuotes[i];
              aq.quote.bankFee = newValue;
            });
          }}
          fullWidth
          size='small'
          variant='outlined'
          InputProps={{
            inputComponent: NumberFormatCustom as any,
          }}
          inputProps={{ style: { textAlign: 'right' } }}
          disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended}
        />
      </Grid>}

      {/* Rate */}
      <Grid item xs={12}>
        <TextField
          label='Rate'
          value={applicationQuote.quote.apr || ''}
          onChange={(e) => {
            updateApplication((app) => {
              const value = Number.parseFloat(e.target.value) || undefined;
              app.applicationQuotes[i].quote.apr = value;

              if (configuration && configuration.featureFlags[FeatureFlagIds.AllowRateEdit] && props.isDealerRetailQuote !== true) {
                app.applicationQuotes[i].quote.apr = value;
                const amountToFinance = calculateAmountToFinanceFromPayment(app.applicationQuotes[i].quote.payment, app.applicationQuotes[i].quote, configuration, payTypes) || undefined;
                app.applicationQuotes[i].quote.amountToFinance = amountToFinance;
              }
            });
          }}
          fullWidth
          size='small'
          variant='outlined'
          InputProps={{
            inputComponent: NumberFormatCustom as any,
          }}
          inputProps={{ style: { textAlign: 'right' } }}
          disabled={disableRate()}
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'apr') !== undefined}
          helperText={getHelperText('apr', props.applicationQuote, currentApp)}
        />
      </Grid>

      {/* Pay Type */}
      <Grid item xs={12}>
        <TextField
          select
          SelectProps={{
            native: true,
          }}
          label='Pay Type'
          value={applicationQuote.quote.quotePayTypeId || ''}
          onChange={(e) => {
            updateApplication((app) => {
              app.applicationQuotes[i].quote.quotePayTypeId = e.target.value;
            });
          }}
          fullWidth
          size='small'
          variant='outlined'
          disabled={props.isDealerRetailQuote !== true && configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true}
        >
          <option>&nbsp;</option>
          {payTypes.map((x, key) => (
            <option key={key} value={x.id}>
              {x.name}
            </option>
          ))}
        </TextField>
      </Grid>

      {/* Term */}
      <Grid item xs={12}>
        <TextField
          label={configuration?.featureFlags[FeatureFlagIds.ShowBiWeeklyTerm] === true ? 'Term (bi-weekly)' : 'Term (months)'}
          value={configuration?.featureFlags[FeatureFlagIds.ShowBiWeeklyTerm] === true ? (multiply(divide(applicationQuote.quote.term, 12), 26) || '') : (applicationQuote.quote.term || '')}
          onChange={(e) => {
            updateApplication((app) => {
              app.applicationQuotes[i].quote.term = Number.parseInt(e.target.value) || 0;

              if (configuration && configuration?.featureFlags[FeatureFlagIds.AllowTermEdit] && props.isDealerRetailQuote !== true && configuration?.clientId !== 'SYL') { // TODO: Fix this so it is not hardcoded. Create Feature Flag PreventRecalculationOfAmountToFinance
                app.applicationQuotes[i].quote.amountToFinance = calculateAmountToFinanceFromPayment(app.applicationQuotes[i].quote.payment, app.applicationQuotes[i].quote, configuration, payTypes) || undefined;
              }
            });
          }}
          fullWidth
          size='small'
          variant='outlined'
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'term') !== undefined}
          helperText={getHelperText('term', props.applicationQuote, currentApp)}
          FormHelperTextProps={getHelperTextProps('term', props.applicationQuote)}
          inputProps={{ style: { textAlign: 'right' } }}
          disabled={disableTerm()}
        />
      </Grid>

      {/* Balloon Term */}
      {configuration?.isFinanceCompany && props.isDealerRetailQuote !== true && (
        <Grid item xs={12}>
          <TextField
            label='Balloon Term (months)'
            value={applicationQuote.quote.balloonTerm || ''}
            onChange={(e) => {
              // TODO: Refactor - dispatch action
              updateApplication((app) => {
                app.applicationQuotes[i].quote.balloonTerm = Number.parseInt(e.target.value) || undefined;
              });
            }}
            fullWidth
            size='small'
            variant='outlined'
            inputProps={{ style: { textAlign: 'right' } }}
            disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended || configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true}
          />
        </Grid>
      )}
      {/* Payment */}
      <Grid item xs={12}>
        <TextField
          label='Payment'
          value={applicationQuote.quote.payment || ''}
          onChange={(e) => {
            if (applicationQuote.quote.quoteType === IndirectQuoteType.Underwriter && configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] === true && props.isDealerRetailQuote !== true) {
              updateApplication((app) => {
                const newValue = nullOrPositiveNumber(e.target.value) || undefined;
                const aq = app.applicationQuotes[i];
                aq.quote.payment = newValue;
                app.applicationQuotes[i].quote.amountToFinance = calculateAmountToFinanceFromPayment(newValue, quote, configuration, payTypes) || undefined;
              });
            }
          }}
          fullWidth
          size='small'
          variant='outlined'
          InputProps={{
            inputComponent: NumberFormatCustom as any,
          }}
          error={appQuote?.ruleViolations?.find((rv) => rv.displayFieldId === 'monthlyPayment') !== undefined}
          helperText={getHelperText('monthlyPayment', props.applicationQuote, currentApp)}
          FormHelperTextProps={getHelperTextProps('monthlyPayment', props.applicationQuote)}
          inputProps={{ style: { textAlign: 'right' } }}
          disabled={applicationQuote.quote.quoteType === IndirectQuoteType.Recommended || configuration?.featureFlags[FeatureFlagIds.AllowPaymentEdit] !== true || props.isDealerRetailQuote === true}
        />
      </Grid>
      {/* Balloon Payment */}
      {props.isDealerRetailQuote !== true && currentApp?.applicationQuotes?.some((x) => (x.quote.balloonTerm ?? 0) > 0) && (
        <Grid item xs={12}>
          <TextField
            label='Balloon Payment'
            style={{ visibility: (applicationQuote.quote.balloonTerm ?? 0) > 0 ? 'visible' : 'hidden' }}
            value={calculateBalloonPayment(applicationQuote.quote) || ''}
            fullWidth
            size='small'
            variant='outlined'
            InputProps={{
              inputComponent: NumberFormatCustom as any,
            }}
            inputProps={{ style: { textAlign: 'right' } }}
            disabled={true}
          />
        </Grid>
      )}
      {/* Options */}
      {/* {props.isDealerRetailQuote !== true && (
      <Grid item xs={12}>
        <TextField
          style={{ visibility: !configuration?.isFinanceCompany || i > 0 ? 'visible' : 'hidden' }}
          label='Options'
          value={applicationQuote.quote.quoteOptions?.filter((x) => !x.deleted)?.map((x) => x.optionId) || []}
          onChange={(e) => {
            updateApplication((app) => {
              const optionIds = ((e.target.value as any) as string[]) || [];
              const quote = app.applicationQuotes[i].quote;
              for (const optionId of optionIds) {
                const quoteOption = quote.quoteOptions?.find((x) => x.optionId === optionId);
                if (quoteOption) {
                  quoteOption.deleted = false;
                } else if (!quote.quoteOptions) {
                  quote.quoteOptions = [
                    {
                      quoteId: quote.id,
                      optionId: optionId,
                    },
                  ];
                } else {
                  quote.quoteOptions.push({
                    quoteId: quote.id,
                    optionId: optionId,
                  });
                }
              }
              // Delete any missing options.
              for (const quoteOption of quote.quoteOptions || []) {
                if (!optionIds.some((optionId) => optionId === quoteOption.optionId)) {
                  quoteOption.deleted = true;
                }
              }
            });
          }}
          size='small'
          variant='outlined'
          fullWidth
          select
          SelectProps={{
            multiple: true,
          }}
        >
          {options?.map((x, key) => (
            <MenuItem key={key} value={x.id || ''}>
              {x.name}
            </MenuItem>
          )) ?? <MenuItem>&nbsp;</MenuItem>}
        </TextField>
      </Grid>
      )} */}
    </Grid>
  );
}
